import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { eur, pick, useContent } from "@/lib/content";

/**
 * Public price configurator. Calls the very same /api/quote the owner's admin
 * quoting desk uses, so a visitor can never be shown a price that differs from
 * what he would quote by hand. Anything outside the configured bounds resolves
 * to an invitation to ask, not to a wrong number.
 */

const Stepper = ({ label, value, min, max, onChange, testid }) => (
  <div className="flex flex-col gap-2">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone">{label}</span>
    <div className="flex items-center gap-3">
      <button
        type="button"
        aria-label="−"
        disabled={value <= min}
        onClick={() => onChange(value - 1)}
        className="h-10 w-10 border border-hairline font-body text-lg text-ink transition-colors hover:border-ink disabled:opacity-30"
      >
        −
      </button>
      <span data-testid={testid} className="w-10 text-center font-display text-3xl text-ink">
        {value}
      </span>
      <button
        type="button"
        aria-label="+"
        disabled={value >= max}
        onClick={() => onChange(value + 1)}
        className="h-10 w-10 border border-hairline font-body text-lg text-ink transition-colors hover:border-ink disabled:opacity-30"
      >
        +
      </button>
    </div>
  </div>
);

export const Configurator = () => {
  const { t, lang } = useLang();
  const { data } = useContent();

  const options = useMemo(
    () => (data?.species ?? []).filter((s) => s.dynamic?.enabled),
    [data],
  );

  const [slug, setSlug] = useState("");
  const [hunters, setHunters] = useState(4);
  const [days, setDays] = useState(3);
  const [extras, setExtras] = useState([]);

  // Default to the first quotable species, and keep the steppers inside its
  // bounds whenever the selection changes.
  useEffect(() => {
    if (!slug && options.length > 0) setSlug(options[0].slug);
  }, [options, slug]);

  const species = options.find((s) => s.slug === slug);
  const cfg = species?.dynamic;

  useEffect(() => {
    if (!cfg) return;
    setHunters((h) => Math.min(Math.max(h, cfg.minHunters), cfg.maxHunters));
    setDays(cfg.standardDays);
    setExtras([]);
  }, [cfg]);

  const quote = useQuery({
    queryKey: ["public-quote", slug, hunters, days, extras],
    queryFn: () =>
      fetch("/api/quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, hunters, days, extras }),
      }).then((r) => r.json()),
    enabled: !!slug && !!cfg,
    placeholderData: (prev) => prev,
  });

  if (options.length === 0) return null;

  const q = quote.data;
  const priced = q && q.customQuote === false;

  return (
    <section id="configurator" data-testid="configurator-section" className="bg-bone">
      <div className="mx-auto max-w-[1200px] px-5 py-24 md:px-10 md:py-32">
        <Reveal>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-rust">
            {t.configurator.overline}
          </span>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-display text-5xl font-medium uppercase leading-[0.9] tracking-tight text-ink md:text-7xl">
            {t.configurator.title}
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl font-body text-lg leading-relaxed text-stone">
            {t.configurator.body}
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-12 grid grid-cols-1 gap-px border border-hairline bg-hairline md:grid-cols-[1.1fr_1fr]">
            <div className="flex flex-col gap-8 bg-bone p-6 md:p-10">
              <div className="flex flex-col gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone">
                  {t.configurator.species}
                </span>
                <div className="flex flex-wrap gap-2">
                  {options.map((s) => (
                    <button
                      key={s.slug}
                      type="button"
                      data-testid={`config-species-${s.slug}`}
                      onClick={() => setSlug(s.slug)}
                      className={`border px-4 py-2 font-body text-[13px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                        s.slug === slug
                          ? "border-ink bg-ink text-bone"
                          : "border-hairline text-stone hover:border-ink hover:text-ink"
                      }`}
                    >
                      {pick(s.names, lang)}
                    </button>
                  ))}
                </div>
              </div>

              {cfg && (
                <>
                  <div className="flex gap-10">
                    <Stepper
                      label={t.configurator.hunters}
                      testid="config-hunters"
                      value={hunters}
                      min={1}
                      max={cfg.maxHunters + 2}
                      onChange={setHunters}
                    />
                    <Stepper
                      label={t.configurator.days}
                      testid="config-days"
                      value={days}
                      min={1}
                      max={cfg.standardDays + cfg.maxExtraDays + 1}
                      onChange={setDays}
                    />
                  </div>

                  {cfg.extras.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone">
                        {t.configurator.extras}
                      </span>
                      {cfg.extras.map((x) => (
                        <label key={x.key} className="flex cursor-pointer items-center gap-3 font-body text-sm text-ink">
                          <input
                            type="checkbox"
                            checked={extras.includes(x.key)}
                            onChange={(e) =>
                              setExtras(e.target.checked
                                ? [...extras, x.key]
                                : extras.filter((k) => k !== x.key))}
                            className="h-4 w-4 accent-olive"
                          />
                          {pick(x.label, lang)}
                          <span className="text-stone">— {eur(x.price, lang)}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex flex-col justify-between gap-6 bg-ink p-6 text-bone md:p-10">
              {priced ? (
                <>
                  <div className="flex flex-col gap-2">
                    {q.lines.map((l) => (
                      <div key={l.key} className="flex items-baseline justify-between gap-4 border-b border-bone/15 pb-2 font-body text-sm">
                        <span className="text-bone/75">{pick(l.label, lang)}</span>
                        <span className="whitespace-nowrap text-bone">{eur(l.amount, lang)}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">
                      {t.configurator.total}
                    </span>
                    <p data-testid="config-total" className="font-display text-5xl leading-none text-bone md:text-6xl">
                      {eur(q.total, lang)}
                    </p>
                    <p className="mt-1 font-body text-sm text-bone/60">
                      {eur(Math.round(q.total / hunters), lang)} {t.configurator.perHunter}
                    </p>
                    <p className="mt-4 font-body text-xs text-bone/50">{t.configurator.note}</p>
                    <a
                      href="#cerere"
                      className="mt-6 inline-flex items-center gap-3 rounded-full bg-bone px-8 py-3.5 font-body text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-colors duration-500 hover:bg-rust hover:text-bone"
                    >
                      {t.configurator.cta} →
                    </a>
                  </div>
                </>
              ) : (
                <div className="flex h-full flex-col justify-center gap-5">
                  <p className="font-body text-lg leading-relaxed text-bone/80">
                    {t.configurator.custom}
                  </p>
                  <a
                    href="#cerere"
                    className="inline-flex items-center gap-3 self-start rounded-full bg-bone px-8 py-3.5 font-body text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-colors duration-500 hover:bg-rust hover:text-bone"
                  >
                    {t.configurator.customCta} →
                  </a>
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
